import React from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Card,
  CardContent,
  CardActions,
  Stack,
  Divider
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Home as HomeIcon,
  BugReport as BugIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      retryCount: 0
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error for debugging and monitoring
    this.setState({
      error,
      errorInfo
    });

    // Log to console for development
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // In production, you would send this to your error reporting service
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService = (error, errorInfo) => {
    // TODO: Integrate with error reporting service (e.g., Sentry, LogRocket)
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      level: this.props.level || 'component',
      retryCount: this.state.retryCount
    };

    // For now, just log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error boundary details:', errorData);
    }

    // TODO: Send to monitoring service
    // errorReportingService.captureException(error, errorData);
  };

  handleRetry = () => {
    this.setState(prevState => ({
      hasError: false,
      error: null,
      errorInfo: null,
      showDetails: false,
      retryCount: prevState.retryCount + 1
    }));

    // Call optional retry callback
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  toggleDetails = () => {
    this.setState(prevState => ({
      showDetails: !prevState.showDetails
    }));
  };

  getErrorTitle = () => {
    const { level } = this.props;
    switch (level) {
      case 'app':
        return 'Application Error';
      case 'page':
        return 'Page Error';
      case 'feature':
        return 'Feature Unavailable';
      case 'component':
      default:
        return 'Something went wrong';
    }
  };

  getErrorMessage = () => {
    const { level, customMessage } = this.props;
    
    if (customMessage) {
      return customMessage;
    }

    switch (level) {
      case 'app':
        return 'The application encountered an unexpected error. Please refresh the page or try again later.';
      case 'page':
        return 'This page encountered an error while loading. You can try refreshing or return to the home page.';
      case 'feature':
        return 'This feature is temporarily unavailable due to an error. Please try again or use other features.';
      case 'component':
      default:
        return 'A component on this page encountered an error. You can continue using other parts of the application.';
    }
  };

  renderFallbackUI = () => {
    const { level, showRetry = true, showHome = true, compact = false } = this.props;
    const { showDetails, error, errorInfo, retryCount } = this.state;

    if (compact) {
      return (
        <Alert 
          severity="error" 
          action={
            showRetry && (
              <Button 
                color="inherit" 
                size="small" 
                onClick={this.handleRetry}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            )
          }
        >
          <Typography variant="body2">
            {this.getErrorMessage()}
          </Typography>
        </Alert>
      );
    }

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={level === 'app' ? '100vh' : '200px'}
        p={2}
      >
        <Card sx={{ maxWidth: 600, width: '100%' }}>
          <CardContent>
            <Stack spacing={2} alignItems="center" textAlign="center">
              <BugIcon color="error" sx={{ fontSize: 48 }} />
              
              <Typography variant="h5" color="error">
                {this.getErrorTitle()}
              </Typography>
              
              <Typography variant="body1" color="text.secondary">
                {this.getErrorMessage()}
              </Typography>

              {retryCount > 0 && (
                <Typography variant="body2" color="warning.main">
                  Retry attempt: {retryCount}
                </Typography>
              )}

              {process.env.NODE_ENV === 'development' && error && (
                <>
                  <Divider sx={{ width: '100%' }} />
                  <Button
                    variant="text"
                    size="small"
                    onClick={this.toggleDetails}
                    endIcon={showDetails ? <CollapseIcon /> : <ExpandIcon />}
                  >
                    {showDetails ? 'Hide' : 'Show'} Error Details
                  </Button>
                  
                  {showDetails && (
                    <Box sx={{ width: '100%', textAlign: 'left' }}>
                      <Typography variant="subtitle2" color="error" gutterBottom>
                        Error Details (Development Only):
                      </Typography>
                      <Box
                        component="pre"
                        sx={{
                          bgcolor: 'grey.100',
                          p: 2,
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          overflow: 'auto',
                          maxHeight: 200,
                          border: '1px solid',
                          borderColor: 'grey.300'
                        }}
                      >
                        <strong>Error:</strong> {error.message}
                        {'\n\n'}
                        <strong>Stack Trace:</strong>
                        {'\n'}
                        {error.stack}
                        {errorInfo && errorInfo.componentStack && (
                          <>
                            {'\n\n'}
                            <strong>Component Stack:</strong>
                            {errorInfo.componentStack}
                          </>
                        )}
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </CardContent>
          
          <CardActions sx={{ justifyContent: 'center', p: 2 }}>
            <Stack direction="row" spacing={2}>
              {showRetry && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={this.handleRetry}
                  startIcon={<RefreshIcon />}
                >
                  Try Again
                </Button>
              )}
              
              {showHome && level !== 'component' && (
                <Button
                  variant="outlined"
                  onClick={this.handleGoHome}
                  startIcon={<HomeIcon />}
                >
                  Go Home
                </Button>
              )}
            </Stack>
          </CardActions>
        </Card>
      </Box>
    );
  };

  render() {
    if (this.state.hasError) {
      // If a custom fallback is provided, use it
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo, this.handleRetry);
      }
      
      // Otherwise, render default fallback UI
      return this.renderFallbackUI();
    }

    // Render children normally if no error
    return this.props.children;
  }
}

export default ErrorBoundary;